/* ---------------------------------------------------------------------------
   HEADER SEARCH

   Queries Shopify's predictive endpoint as the shopper types and renders three
   groups of text links. Nothing here is a product card -- the panel is a way to
   get somewhere, so it stays a list.

   Brands are read off the vendors of the products that matched, rather than
   from a generated index. That works because of a catalogue convention: the
   brand is part of every product title, so a product of that brand always
   matches the same text the brand name does. A separate index would be one more
   file to regenerate after every catalogue change, for no extra coverage.
   --------------------------------------------------------------------------- */
(function () {
  const ENDPOINT = '/search/suggest.json';

  /* True unless the thing mentions a tester. Covers suggested search terms
     ({text}) and products ({title, handle}). */
  const TESTER = /\btester\b|\btstr\b/i;
  const notTester = (item) =>
    !TESTER.test(item && (item.text || item.title || '') ) && !TESTER.test((item && item.handle) || '');
  const DEBOUNCE = 180;
  const MIN_CHARS = 2;

  const ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => ENTITIES[ch]);

  /* The matched run is marked rather than each matching letter, so "de marly"
     bolds as one span instead of as scattered characters. */
  function highlight(text, query) {
    const subject = String(text || '');
    const needle = String(query || '').trim();
    if (!needle) return escapeHtml(subject);
    const at = subject.toLowerCase().indexOf(needle.toLowerCase());
    if (at < 0) return escapeHtml(subject);
    return (
      escapeHtml(subject.slice(0, at)) +
      '<mark>' + escapeHtml(subject.slice(at, at + needle.length)) + '</mark>' +
      escapeHtml(subject.slice(at + needle.length))
    );
  }

  class SynergySearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.syn-search__form');
      this.input = this.querySelector('.syn-search__input');
      this.panel = this.querySelector('[data-syn-search-panel]');
      this.clearBtn = this.querySelector('[data-syn-search-clear]');
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.timer = null;
      this.controller = null;
      this.lastQuery = '';

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => { if (this.panel.innerHTML) this.open(); });
      this.input.addEventListener('keydown', (event) => this.onKey(event));
      this.clearBtn.addEventListener('click', () => this.clear());
      this.form.addEventListener('submit', () => this.close());

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });

      this.syncClear();
    }

    syncClear() {
      this.clearBtn.hidden = this.input.value.trim().length === 0;
    }

    onInput() {
      this.syncClear();
      const query = this.input.value.trim();
      window.clearTimeout(this.timer);
      if (query.length < MIN_CHARS) {
        this.panel.innerHTML = '';
        this.close();
        return;
      }
      this.timer = window.setTimeout(() => this.request(query), DEBOUNCE);
    }

    async request(query) {
      if (query === this.lastQuery && this.panel.innerHTML) { this.open(); return; }
      this.lastQuery = query;

      /* One request in flight: responses arriving out of order would show
         suggestions for a query the shopper has already typed past. */
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,query',
        'resources[limit]': '10',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,vendor,product_type,variants.title',
      });

      let payload;
      try {
        const response = await fetch(ENDPOINT + '?' + params, { signal: this.controller.signal });
        if (!response.ok) return;
        payload = await response.json();
      } catch (error) {
        return;                    // aborted or offline: leave the panel as it was
      }

      const results = (payload && payload.resources && payload.resources.results) || {};
      /* Testers are not sold here. Shopify's suggested SEARCH TERMS come from
         what shoppers have typed, not from the catalogue, so "… tester" keeps
         being offered even though every tester product is draft or archived.
         Filtered on both lists: a term can be suggested with no product behind
         it, and a product could be republished by mistake. */
      this.render(query, (results.queries || []).filter(notTester), (results.products || []).filter(notTester));
    }

    render(query, queries, products) {
      const lower = query.toLowerCase();
      const groups = [];

      const suggestions = queries
        .filter((item) => item.text && item.text.toLowerCase() !== lower)
        .slice(0, 5);
      if (suggestions.length) {
        groups.push(this.group('Matching Suggestions', suggestions.map((item) => ({
          href: this.searchHref(item.text),
          label: highlight(item.text, query),
        }))));
      }

      /* A vendor earns a row only when the brand name is what matched. Listing
         the brands of whatever happened to come back is noise, not a shortcut. */
      const seen = new Set();
      const brands = [];
      for (const product of products) {
        const vendor = (product.vendor || '').trim();
        const key = vendor.toLowerCase();
        if (!vendor || seen.has(key)) continue;
        if (!key.includes(lower) && !lower.includes(key)) continue;
        seen.add(key);
        brands.push({ href: this.brandHref(vendor), label: highlight(vendor, query) });
      }
      if (brands.length) groups.push(this.group('Brands', brands.slice(0, 5)));

      const items = products.slice(0, 6).map((product) => ({
        href: product.url,
        label: highlight(product.title, query),
        meta: product.product_type || '',
      }));
      if (items.length) groups.push(this.group('Products', items));

      this.panel.innerHTML = groups.length
        ? groups.join('')
        : '<p class="syn-search__empty">No matches for &ldquo;' + escapeHtml(query) + '&rdquo;</p>';
      this.open();
    }

    group(title, rows) {
      return (
        '<div class="syn-search__group">' +
          '<h2 class="syn-search__group-title">' + escapeHtml(title) + '</h2>' +
          '<ul class="syn-search__list">' +
            rows.map((row) =>
              '<li><a class="syn-search__link" href="' + escapeHtml(row.href) + '" role="option">' +
                '<span class="syn-search__link-label">' + row.label + '</span>' +
                (row.meta
                  ? '<span class="syn-search__link-meta">' + escapeHtml(row.meta) + '</span>'
                  : '') +
              '</a></li>'
            ).join('') +
          '</ul>' +
        '</div>'
      );
    }

    searchHref(text) {
      return this.searchUrl + '?q=' + encodeURIComponent(text) +
             '&type=product&options%5Bprefix%5D=last';
    }

    /* vendor:"..." is search syntax rather than a facet, so the brand link needs
       no filter configured and no collection to exist for that brand. */
    brandHref(vendor) {
      return this.searchUrl + '?q=' + encodeURIComponent('vendor:"' + vendor + '"') +
             '&type=product';
    }

    open() {
      if (!this.panel.innerHTML) return;
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
    }

    clear() {
      this.input.value = '';
      this.panel.innerHTML = '';
      this.lastQuery = '';
      this.syncClear();
      this.close();
      this.input.focus();
    }

    onKey(event) {
      if (event.key === 'Escape') { this.close(); return; }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const links = Array.prototype.slice.call(this.panel.querySelectorAll('.syn-search__link'));
      if (!links.length) return;
      event.preventDefault();
      const at = links.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown'
        ? (at + 1) % links.length
        : (at <= 0 ? links.length - 1 : at - 1);
      links[next].focus();
    }
  }

  if (!customElements.get('synergy-search')) {
    customElements.define('synergy-search', SynergySearch);
  }
})();
